import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Col, Row} from "antd";

import ErrorBoundary from "../../../../components/ErrorBoundary";
import AccessKeyTable from "../../../../components/accessKey/AccessKeyTable";
import AccessKeyHistory from "../../../../components/accessKey/AccessKeyHistory";
import {messageType, showMessages} from "../../../../helpers/message";
import {blessFob, getFobs, lostFob, revokeFob} from "../../../../helpers/fob";

const AccessKeyStaff = () => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [loading, setLoading] = useState(false);

    const [fobs, setFobs] = useState([]);
    const [fobsNeedRefresh, setFobsNeedRefresh] = useState(false);

    const toggleFobsNeedRefresh = useCallback(() => {
        setFobsNeedRefresh(!fobsNeedRefresh);
    }, [fobsNeedRefresh]);

    const handleRevokeFob = useCallback(async (fobID) => {
        const abortSignal = abortSignalRef.current;
        setLoading(true);

        try {
            await revokeFob(fobID, abortSignal);

            toggleFobsNeedRefresh();
            setLoading(false);
            showMessages('Access Key has been successfully unassigned.', messageType.success)
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    }, [toggleFobsNeedRefresh]);

    const handleBlessFob = useCallback(async (fob) => {
        const abortSignal = abortSignalRef.current;
        setLoading(true);

        try {
            await blessFob(fob.id, abortSignal);

            toggleFobsNeedRefresh();
            setLoading(false);

            showMessages(`Access Key has been successfully ${fob.is_blessed ? 'unblessed' : 'blessed'}.`, messageType.success)
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    }, [toggleFobsNeedRefresh]);

    const handleLostFob = useCallback(async (fobID) => {
        const abortSignal = abortSignalRef.current;
        setLoading(true);

        try {
            await lostFob(fobID, abortSignal);

            toggleFobsNeedRefresh();
            setLoading(false);
            showMessages('Access Key has been successfully reported lost/stolen.', messageType.success)
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    }, [toggleFobsNeedRefresh]);

    // Fetch fobs on component did mount.
    useEffect(() => {
        const fetchFobs = async () => {
            setLoading(true);

            try {
                const abortSignal = abortSignalRef.current
                const response = await getFobs(abortSignal);

                if (abortSignal.aborted === false) {
                    setFobs(response.map(object => {
                        return {
                            ...object,
                            actions: ['bless', 'lost', 'delete'],
                            deleteFunction: handleRevokeFob,
                            blessFunction: handleBlessFob,
                            lostFunction: handleLostFob,
                        }
                    }))
                    setLoading(false);
                }

            } catch (error) {
                showMessages(error.message, messageType.error);
                setLoading(false);
                if(error.status_code === 404) window.location.replace('/404');
            }
        }

        fetchFobs()
    }, [fobsNeedRefresh, handleBlessFob, handleLostFob, handleRevokeFob]);

    return (
        <>
            <ErrorBoundary>
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={14} lg={14} xl={14}>
                        <AccessKeyTable
                            fobs={fobs}
                            loading={loading}
                            refreshFobs={toggleFobsNeedRefresh}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={10} lg={10} xl={8}>
                        <AccessKeyHistory
                            refreshFobs={fobsNeedRefresh}
                        />
                    </Col>
                </Row>
            </ErrorBoundary>
        </>
    );
}

export default AccessKeyStaff

AccessKeyStaff.propTypes = {
};
